import {
  StyleParamType,
  wixColorParam,
  wixFontParam,
  createStylesParams,
  ISiteTextPreset,
  createStylesParam,
} from '@wix/tpa-settings';

export type IStylesParams = {
  topAndBottomPadding: StyleParamType.Number;
  sidePadding: StyleParamType.Number;
  widgetBorderColor: StyleParamType.Color;
  widgetBorderWidth: StyleParamType.Number;
  widgetBorderRadius: StyleParamType.Number;
  widgetBackgroundColor: StyleParamType.Color;
  headerBackgroundColor: StyleParamType.Color;
  dividerWidth: StyleParamType.Number;
  dividerColor: StyleParamType.Color;
  sectionsGap: StyleParamType.Number;
  pageTitleFont: StyleParamType.Font;
  pageTitleColor: StyleParamType.Color;
  sectionTitleFont: StyleParamType.Font;
  sectionTitleColor: StyleParamType.Color;
  primaryTextFont: StyleParamType.Font;
  primaryTextColor: StyleParamType.Color;
  secondaryTextFont: StyleParamType.Font;
  secondaryTextColor: StyleParamType.Color;
  actionColor: StyleParamType.Color;
  disabledColor: StyleParamType.Color;
  borderWidth: StyleParamType.Number;
  borderRadius: StyleParamType.Number;
  dateTextFont: StyleParamType.Font;
  dateTextColor: StyleParamType.Color;
  timeSelectionTextColor: StyleParamType.Color;
  timeSelectionTextFont: StyleParamType.Font;
  timeSelectionBackgroundColor: StyleParamType.Color;
  bookButtonTextColor: StyleParamType.Color;
  bookButtonBGColor: StyleParamType.Color;
  bookButtonBorderColor: StyleParamType.Color;
  bookButtonHoverBorderColor: StyleParamType.Color;
  bookButtonHoverTextColor: StyleParamType.Color;
  bookButtonHoverBGColor: StyleParamType.Color;
  bookButtonTextFont: StyleParamType.Font;
  bookButtonBGColorOpacity: StyleParamType.Number;
  bookButtonHoverBGColorOpacity: StyleParamType.Number;
  bookButtonBorderColorOpacity: StyleParamType.Number;
  bookButtonHoverBorderColorOpacity: StyleParamType.Number;
  slotBackgroundColor: StyleParamType.Color;
  slotPrimaryTextFont: StyleParamType.Font;
  slotPrimaryTextColor: StyleParamType.Color;
  slotNameTextFont: StyleParamType.Font;
  slotNameTextColor: StyleParamType.Color;
  slotTimeTextFont: StyleParamType.Font;
  slotTimeTextColor: StyleParamType.Color;
  slotSecondaryTextFont: StyleParamType.Font;
  slotSecondaryTextColor: StyleParamType.Color;
  slotBorderWidth: StyleParamType.Number;
  slotBorderColor: StyleParamType.Color;
  servicePrimaryTextColor0: StyleParamType.Color;
  servicePrimaryTextColor1: StyleParamType.Color;
  servicePrimaryTextColor2: StyleParamType.Color;
  servicePrimaryTextColor3: StyleParamType.Color;
  servicePrimaryTextColor4: StyleParamType.Color;
  servicePrimaryTextColor5: StyleParamType.Color;
  servicePrimaryTextColor6: StyleParamType.Color;
  servicePrimaryTextColor7: StyleParamType.Color;
  servicePrimaryTextColor8: StyleParamType.Color;
  servicePrimaryTextColor9: StyleParamType.Color;
  serviceBackgroundColor0: StyleParamType.Color;
  serviceBackgroundColor1: StyleParamType.Color;
  serviceBackgroundColor2: StyleParamType.Color;
  serviceBackgroundColor3: StyleParamType.Color;
  serviceBackgroundColor4: StyleParamType.Color;
  serviceBackgroundColor5: StyleParamType.Color;
  serviceBackgroundColor6: StyleParamType.Color;
  serviceBackgroundColor7: StyleParamType.Color;
  serviceBackgroundColor8: StyleParamType.Color;
  serviceBackgroundColor9: StyleParamType.Color;
  mobilePageTitleFontSize: StyleParamType.Number;
  mobileSectionTitleFontSize: StyleParamType.Number;
  mobilePrimaryTextFontSize: StyleParamType.Number;
  mobileSecondaryTextFontSize: StyleParamType.Number;
  mobileDateTextFontSize: StyleParamType.Number;
  mobileTimeSelectionTextFontSize: StyleParamType.Number;
  mobileBookButtonTextFontSize: StyleParamType.Number;
  mobileSlotPrimaryTextFontSize: StyleParamType.Number;
  mobileSlotTimeTextFontSize: StyleParamType.Number;
  mobileSlotNameTextFontSize: StyleParamType.Number;
  mobileSlotSecondaryTextFontSize: StyleParamType.Number;
};

const slotPrimaryTextFont = createStylesParam('slotPrimaryTextFont', {
  type: StyleParamType.Font,
  getDefaultValue: ({ textPresets }: { textPresets: ISiteTextPreset }) =>
    wixFontParam('Body-M', {
      size: 16,
    })({ textPresets }),
});

const slotPrimaryTextColor = createStylesParam('slotPrimaryTextColor', {
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam('color-5'),
});

const mobileSlotPrimaryTextFontSize = createStylesParam(
  'mobileSlotPrimaryTextFontSize',
  {
    type: StyleParamType.Number,
    getDefaultValue: () => 14,
  },
);

export default createStylesParams<IStylesParams>({
  topAndBottomPadding: {
    type: StyleParamType.Number,
    inheritDesktop: false,
    getDefaultValue: ({ isMobile }) => (isMobile ? 24 : 48),
  },
  sidePadding: {
    type: StyleParamType.Number,
    inheritDesktop: false,
    getDefaultValue: () => 20,
  },
  widgetBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  widgetBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.2),
  },
  widgetBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  widgetBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  headerBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
  },
  dividerWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  dividerColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.2),
  },
  sectionsGap: {
    type: StyleParamType.Number,
    inheritDesktop: false,
    getDefaultValue: () => 44,
  },
  pageTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  pageTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Page-title', { size: 32 }),
  },
  sectionTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  sectionTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Page-title', { size: 20 }),
  },
  primaryTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  primaryTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({
      textPresets,
      isMobile,
    }: {
      textPresets: ISiteTextPreset;
      isMobile: boolean;
    }) =>
      wixFontParam('Body-M', {
        size: isMobile ? 14 : 16,
      })({ textPresets }),
  },
  secondaryTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-4'),
  },
  secondaryTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({
      textPresets,
    }: {
      textPresets: ISiteTextPreset;
      isMobile: boolean;
    }) =>
      wixFontParam('Body-M', {
        size: 14,
      })({ textPresets }),
  },
  actionColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  disabledColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-3'),
  },
  borderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  borderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
  },
  dateTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  dateTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({
      textPresets,
      isMobile,
    }: {
      textPresets: ISiteTextPreset;
      isMobile: boolean;
    }) =>
      wixFontParam('Body-M', {
        size: isMobile ? 14 : 16,
      })({ textPresets }),
  },
  timeSelectionTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({
      textPresets,
      isMobile,
    }: {
      textPresets: ISiteTextPreset;
      isMobile: boolean;
    }) =>
      wixFontParam('Body-M', {
        size: isMobile ? 14 : 16,
      })({ textPresets }),
  },
  timeSelectionTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
  },
  timeSelectionBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
  },
  bookButtonTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  bookButtonBGColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  bookButtonHoverBGColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  bookButtonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  bookButtonHoverBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  bookButtonHoverTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
  },
  bookButtonTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({
      textPresets,
      isMobile,
    }: {
      textPresets: ISiteTextPreset;
      isMobile: boolean;
    }) =>
      wixFontParam('Body-M', {
        size: isMobile ? 14 : 16,
      })({ textPresets }),
  },
  bookButtonBGColorOpacity: {
    type: StyleParamType.Number,
    getDefaultValue: () => 100,
  },
  bookButtonHoverBGColorOpacity: {
    type: StyleParamType.Number,
    getDefaultValue: () => 70,
  },
  bookButtonBorderColorOpacity: {
    type: StyleParamType.Number,
    getDefaultValue: () => 100,
  },
  bookButtonHoverBorderColorOpacity: {
    type: StyleParamType.Number,
    getDefaultValue: () => 70,
  },
  slotPrimaryTextFont,
  slotPrimaryTextColor,
  slotNameTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(slotPrimaryTextFont),
  },
  slotNameTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(slotPrimaryTextColor),
  },
  slotTimeTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(slotPrimaryTextFont),
  },
  slotTimeTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(slotPrimaryTextColor),
  },
  slotSecondaryTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: ({ textPresets }: { textPresets: ISiteTextPreset }) =>
      wixFontParam('Body-M', {
        size: 14,
      })({ textPresets }),
  },
  slotSecondaryTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-4'),
  },
  mobilePageTitleFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 32,
  },
  mobileSectionTitleFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 20,
  },
  mobilePrimaryTextFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 14,
  },
  mobileSecondaryTextFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 14,
  },
  mobileDateTextFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 14,
  },
  mobileTimeSelectionTextFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 14,
  },
  mobileBookButtonTextFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 14,
  },
  mobileSlotPrimaryTextFontSize,
  mobileSlotNameTextFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(mobileSlotPrimaryTextFontSize),
  },
  mobileSlotTimeTextFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: ({ getStyleParamValue }) =>
      getStyleParamValue(mobileSlotPrimaryTextFontSize),
  },
  mobileSlotSecondaryTextFontSize: {
    type: StyleParamType.Number,
    getDefaultValue: () => 14,
  },
  slotBackgroundColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1', 0),
  },
  slotBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
  },
  slotBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
  },
  servicePrimaryTextColor0: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  servicePrimaryTextColor1: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  servicePrimaryTextColor2: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  servicePrimaryTextColor3: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  servicePrimaryTextColor4: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  servicePrimaryTextColor5: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  servicePrimaryTextColor6: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  servicePrimaryTextColor7: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  servicePrimaryTextColor8: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  servicePrimaryTextColor9: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
  },
  serviceBackgroundColor0: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.1),
  },
  serviceBackgroundColor1: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.1),
  },
  serviceBackgroundColor2: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.1),
  },
  serviceBackgroundColor3: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.1),
  },
  serviceBackgroundColor4: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.1),
  },
  serviceBackgroundColor5: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.1),
  },
  serviceBackgroundColor6: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.1),
  },
  serviceBackgroundColor7: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.1),
  },
  serviceBackgroundColor8: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.1),
  },
  serviceBackgroundColor9: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8', 0.1),
  },
});
